import type { Dispatch, SetStateAction } from "react";
import { isAxiosError } from "axios";
import type { TFunction } from "i18next";
import type { ConsentGovernanceItem } from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import {
    getConsentGovernanceErrorMessage,
    useDeleteAdminConsentItem,
    useDeleteAdminConsentItemVersion,
    usePatchAdminConsentItem,
} from "src/api/client-api/consent/useManageConsentGovernanceAdmin";
import type { ConsentDeleteTarget } from "../consentGovernanceTypes";

type SnackbarApi = {
    setSnackbarProps: (props: { message: string; type: "error" | "success" }) => void;
};

type UseConsentGovernanceActionsInput = {
    closeDeleteDialog: () => void;
    deleteTarget?: ConsentDeleteTarget;
    setSelectedItemIds: Dispatch<SetStateAction<Set<number>>>;
    setSelectedVersionIds: Dispatch<SetStateAction<Set<number>>>;
    snackbar: SnackbarApi;
    t: TFunction;
};

export const useConsentGovernanceActions = ({
    closeDeleteDialog,
    deleteTarget,
    setSelectedItemIds,
    setSelectedVersionIds,
    snackbar,
    t,
}: UseConsentGovernanceActionsInput) => {
    const deleteItemMutation = useDeleteAdminConsentItem();
    const deleteVersionMutation = useDeleteAdminConsentItemVersion();
    const patchItemMutation = usePatchAdminConsentItem();
    const bulkActionLoading =
        deleteItemMutation.isLoading || deleteVersionMutation.isLoading || patchItemMutation.isLoading;

    const handleToggleItemActive = async (item: ConsentGovernanceItem, active: boolean) => {
        try {
            await patchItemMutation.mutateAsync({
                id: item.id,
                data: { active },
            });
            snackbar.setSnackbarProps({
                message:
                    active ?
                        t("settings.consent.item.visible_success", "Purpose made visible.")
                    :   t("settings.consent.item.hidden_success", "Purpose hidden."),
                type: "success",
            });
        } catch (error) {
            snackbar.setSnackbarProps({
                message:
                    error instanceof Error ?
                        error.message
                    :   t("settings.consent.item.visibility_error", "Failed to update visibility."),
                type: "error",
            });
        }
    };

    const handleBulkSetOptInsActive = async (items: ConsentGovernanceItem[], active: boolean) => {
        try {
            await Promise.all(
                items.map((item) =>
                    patchItemMutation.mutateAsync({
                        id: item.id,
                        data: { active },
                    }),
                ),
            );
            setSelectedItemIds(new Set());
            snackbar.setSnackbarProps({
                message:
                    active ?
                        t("settings.consent.bulk.visible_success", "{{count}} purpose(s) made visible.", {
                            count: items.length,
                        })
                    :   t("settings.consent.bulk.hidden_success", "{{count}} purpose(s) hidden.", {
                            count: items.length,
                        }),
                type: "success",
            });
        } catch (error) {
            snackbar.setSnackbarProps({
                message:
                    error instanceof Error ?
                        error.message
                    :   t("settings.consent.bulk.visibility_error", "Failed to update purposes."),
                type: "error",
            });
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) {
            return;
        }

        try {
            if (deleteTarget.kind === "item") {
                await deleteItemMutation.mutateAsync(deleteTarget.item.id);
                snackbar.setSnackbarProps({
                    message:
                        deleteTarget.item.type === "document" ?
                            t("settings.consent.delete.document_success", "Document deleted successfully")
                        :   t("settings.consent.delete.preference_success", "Preference deleted successfully"),
                    type: "success",
                });
                setSelectedItemIds((current) => {
                    const next = new Set(current);
                    next.delete(deleteTarget.item.id);
                    return next;
                });
            } else if (deleteTarget.kind === "items") {
                for (const item of deleteTarget.items) {
                    await deleteItemMutation.mutateAsync(item.id);
                }
                setSelectedItemIds(new Set());
                snackbar.setSnackbarProps({
                    message: t("settings.consent.bulk.delete_items_success", "{{count}} item(s) deleted.", {
                        count: deleteTarget.items.length,
                    }),
                    type: "success",
                });
            } else if (deleteTarget.kind === "version") {
                await deleteVersionMutation.mutateAsync(deleteTarget.version.id);
                snackbar.setSnackbarProps({
                    message: t("settings.consent.delete.version_success", "Version deleted successfully"),
                    type: "success",
                });
                setSelectedVersionIds((current) => {
                    const next = new Set(current);
                    next.delete(deleteTarget.version.id);
                    return next;
                });
            } else {
                for (const version of deleteTarget.versions) {
                    await deleteVersionMutation.mutateAsync(version.id);
                }
                setSelectedVersionIds(new Set());
                snackbar.setSnackbarProps({
                    message: t("settings.consent.bulk.delete_versions_success", "{{count}} version(s) deleted.", {
                        count: deleteTarget.versions.length,
                    }),
                    type: "success",
                });
            }

            closeDeleteDialog();
        } catch (error) {
            const fallbackMessage =
                deleteTarget.kind === "item" || deleteTarget.kind === "items" ?
                    isAxiosError(error) && error.response?.status === 409 ?
                        t(
                            "settings.consent.delete.item_blocked_versions",
                            "This item cannot be deleted while versions still exist. Delete its versions first.",
                        )
                    :   t("settings.consent.delete.item_error", "Failed to delete this item.")
                : isAxiosError(error) && error.response?.status === 409 ?
                    t(
                        "settings.consent.delete.version_blocked_records",
                        "This version cannot be deleted because audit records already exist.",
                    )
                :   t("settings.consent.delete.version_error", "Failed to delete this version.");
            snackbar.setSnackbarProps({
                message: getConsentGovernanceErrorMessage(error, t, fallbackMessage),
                type: "error",
            });
        }
    };

    return {
        bulkActionLoading,
        handleBulkSetOptInsActive,
        handleDeleteConfirm,
        handleToggleItemActive,
        isDeleting: deleteItemMutation.isLoading || deleteVersionMutation.isLoading,
        isPatchPending: patchItemMutation.isLoading,
    };
};
